import type { PaymentMethod } from "./types.js";

/** Minimum lead time before effectiveAt for self-service cancellation reversal. */
export const CANCELLATION_SELF_SERVICE_REVERSAL_MIN_MS = 2 * 60 * 60 * 1000;

/** True when a scheduled cancellation can be reversed in-app (not via support). */
export function canSelfServiceReverseScheduledCancellation(
  change: { kind: string; effectiveAt: string },
  now: Date = new Date(),
  opts?: { paymentMethod?: PaymentMethod | null },
): boolean {
  if (change.kind !== "cancellation") return false;
  // OpenPix and Stone recurrences are cancelled immediately at schedule time;
  // reversing needs a new agreement — support only.
  if (opts?.paymentMethod === "openpix" || opts?.paymentMethod === "stone") return false;
  const effectiveAt = new Date(change.effectiveAt).getTime();
  if (Number.isNaN(effectiveAt)) return false;
  return effectiveAt - now.getTime() >= CANCELLATION_SELF_SERVICE_REVERSAL_MIN_MS;
}

/** Scheduled change waiting for Stripe/OpenPix authorization — not shown as "agendada" yet. */
export function isScheduledChangePendingApproval(row: {
  providerSwapStatus?: string | null;
  pendingOrderId?: string | null;
}): boolean {
  return row.providerSwapStatus === "pending_approval" && !!row.pendingOrderId;
}

/** Approved (or legacy) scheduled changes visible to the customer. */
export function isVisibleScheduledChange(row: {
  providerSwapStatus?: string | null;
  pendingOrderId?: string | null;
}): boolean {
  return !isScheduledChangePendingApproval(row);
}

export function scheduledChangePayloadMatches(
  payload: Record<string, unknown> | null | undefined,
  target: { target_plan: string; target_interval: string; target_payment_method: string },
): boolean {
  if (!payload) return false;
  return (
    payload.target_plan === target.target_plan &&
    payload.target_interval === target.target_interval &&
    payload.target_payment_method === target.target_payment_method
  );
}

export type InPlaceRevertBlock =
  | "not_open"
  | "cancellation"
  | "not_approved"
  | "not_in_place"
  | "not_card"
  | "not_scheduled"
  | "order_not_paid"
  | "no_original_price"
  | "too_close";

/**
 * Whether support can undo a scheduled card change that was approved in place on the customer's
 * live Stripe subscription: Stripe already holds the new price from the effective date, so undoing
 * it means putting the current price back there too. Not within 2 h of the effective date, when
 * the scheduled-change job may be applying it.
 */
export function canRevertInPlaceScheduledChange(
  args: {
    row: {
      kind: string;
      effectiveAt: Date;
      appliedAt: Date | null;
      canceledAt: Date | null;
      providerSwapStatus: string | null;
      newProviderSubscriptionId: string | null;
      payload: unknown;
    };
    sub: { paymentMethod: string; status: string; providerSubscriptionId: string | null };
    order: { status: string; providerOrderId: string | null } | null;
  },
  now: Date = new Date(),
): { ok: true } | { ok: false; reason: InPlaceRevertBlock } {
  const { row, sub, order } = args;
  if (row.appliedAt || row.canceledAt) return { ok: false, reason: "not_open" };
  if (row.kind === "cancellation") return { ok: false, reason: "cancellation" };
  if (row.providerSwapStatus !== "approved") return { ok: false, reason: "not_approved" };
  if (!row.newProviderSubscriptionId || row.newProviderSubscriptionId !== sub.providerSubscriptionId) {
    return { ok: false, reason: "not_in_place" };
  }
  if (!order?.providerOrderId?.startsWith("in_place_")) return { ok: false, reason: "not_in_place" };
  if (sub.paymentMethod !== "stripe") return { ok: false, reason: "not_card" };
  if (sub.status !== "scheduled_change") return { ok: false, reason: "not_scheduled" };
  if (order.status !== "paid") return { ok: false, reason: "order_not_paid" };
  if (!inPlaceRevertPrice(row.payload)) return { ok: false, reason: "no_original_price" };
  if (row.effectiveAt.getTime() - now.getTime() < CANCELLATION_SELF_SERVICE_REVERSAL_MIN_MS) {
    return { ok: false, reason: "too_close" };
  }
  return { ok: true };
}

/**
 * The Stripe price the subscription had before a scheduled change lowered it in place, stored on
 * the row when it was approved. The list price would charge a customer with a coupon or offer more.
 */
export function inPlaceRevertPrice(
  payload: unknown,
): { unitAmount: number; currency: string; interval: "month" | "year" } | null {
  const price = (payload as { previous_recurring_price?: Record<string, unknown> } | null)?.previous_recurring_price;
  if (!price) return null;
  const { unitAmount, currency, interval } = price;
  if (typeof unitAmount !== "number" || !(unitAmount > 0)) return null;
  if (typeof currency !== "string" || !currency) return null;
  if (interval !== "month" && interval !== "year") return null;
  return { unitAmount, currency, interval };
}
